import React from 'react';
import { useActivityImage } from '../../hooks/useActivityImage';
import { thumbCredit, creditHref } from '../../utils/unsplashCredit';
import type { ActivityImage } from '../../types';

/**
 * The venue photo for one activity of a SHARED plan.
 *
 * Explore is the surface the whole place-keyed design exists for: an itinerary
 * you are browsing belongs to someone else, which is why the photo endpoint is
 * keyed on the place rather than on the item. The membership rows captured at
 * share time are what make this request answerable.
 *
 * Community activities carry no pool photo of their own yet — that is Phase 3 —
 * so the ladder here is venue, then nothing. Nothing is a perfectly good answer:
 * the day already has its own image above these rows.
 */
export const CommunityActivityThumb: React.FC<{
  placeId?: string;
  pool?: ActivityImage['pool'];
  name?: string;
}> = ({ placeId, pool, name }) => {
  const image = useActivityImage(
    placeId || pool ? { place_id: placeId, pool } : null, 200,
  );
  if (!image.url) return null;

  const credit = image.photographer
    ? thumbCredit(image.photographer, image.tier)
    : 'View this photo at its source';
  const thumb = (
    <img
      className="community-activity-thumb"
      src={image.url}
      alt={name || ''}
      loading="lazy"
      onError={image.onError}
    />
  );
  // No link to credit through to (a venue photo Google returned without a
  // Maps URI): the credit still has to be readable, so carry it on a labelled
  // wrapper rather than dropping it. Mirrors what mobile does for this case.
  if (!image.creditUrl) {
    return image.photographer ? (
      <span title={credit}>{thumb}<span className="sr-only">{credit}</span></span>
    ) : thumb;
  }
  return (
    <a
      href={creditHref(image.creditUrl, image.tier)}
      target="_blank"
      rel="noopener noreferrer nofollow"
      title={credit}
      onClick={(e) => e.stopPropagation()}
      className="community-activity-thumb-link"
    >
      {thumb}
      <span className="sr-only">{credit}</span>
    </a>
  );
};

export default CommunityActivityThumb;
